import { useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import Picker from '../../../common/components/picker/picker';
import { useRepositoriesList } from '../../repositories/contexts/repositories';
import { searchTeams } from '../../teams/contexts/teams';
import { useTiersList } from '../../tiers/contexts/tiers';
import {
    useAssignTeam,
    useAssignmentPreview,
    useProjectAssignments,
    useScopeAxis,
    useUnassignTeam,
} from '../contexts/assignments';

import '../styles/assignments.css';

// Which teams reach this project, and how far.
//
// THE ONE THING THIS SCREEN EXISTS TO SAY: an empty axis is NOT no access — it
// is no restriction on that axis. Removing the last repository from the list
// WIDENS the assignment to the whole project. It is the same trap the backend
// writes about for scope columns that admit NULL, and the place where somebody
// clicks "quitar" believing they are revoking.
//
// The sentence under each assignment is the backend's own (`scope.explanation`)
// and is not rewritten here on purpose: two wordings of the same rule is how
// the panel and the API end up explaining it differently.

const PICKER_LIMIT = 100;

function ScopeAxis({ title, chips, options, optionLabel, onAdd, onRemove, busy, emptyMeaning }) {
    const [choice, setChoice] = useState('');

    const handleAdd = () => {
        if (!choice) return;
        onAdd(choice);
        setChoice('');
    };

    return (
        <div className="axis">
            <div className="axis__head">
                <span className="axis__title">{title}</span>
                {!chips.length && <span className="axis__empty">{emptyMeaning}</span>}
            </div>

            {!!chips.length && (
                <ul className="axis__chips">
                    {chips.map(chip => (
                        <li className="axis__chip" key={chip.id}>
                            {chip.label}
                            <Gate domain="projects:write">
                                <button
                                    type="button"
                                    className="axis__chip-remove"
                                    onClick={() => onRemove(chip.id)}
                                    disabled={busy}
                                    aria-label={`Sacar ${chip.label} del eje`}
                                >
                                    ×
                                </button>
                            </Gate>
                        </li>
                    ))}
                </ul>
            )}

            <Gate domain="projects:write">
                <div className="axis__add">
                    <select
                        className="axis__select"
                        value={choice}
                        onChange={e => setChoice(e.target.value)}
                        disabled={busy}
                    >
                        <option value="">Angostar a...</option>
                        {options.map(option => (
                            <option key={option.id} value={option.id}>{optionLabel(option)}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="axis__add-button"
                        onClick={handleAdd}
                        disabled={!choice || busy}
                    >
                        Agregar
                    </button>
                </div>
            </Gate>
        </div>
    );
}

function Preview({ assignmentId }) {
    const [open, setOpen] = useState(false);
    const { data, isLoading } = useAssignmentPreview(assignmentId, { enabled: open });

    return (
        <div className="preview">
            <button type="button" className="preview__toggle" onClick={() => setOpen(v => !v)}>
                {open ? 'Ocultar el alcance real' : 'Ver a qué tareas alcanza'}
            </button>

            {open && isLoading && <p className="preview__note">Calculando...</p>}

            {open && data && (
                <div className="preview__body">
                    <p className="preview__total">
                        <strong>{data.reach?.total ?? 0}</strong> tareas alcanzadas
                        {data.reach?.truncated && ` · se muestran ${data.reach.shown}`}
                    </p>

                    {/* The backend explains an empty result itself, because that
                        is the moment somebody concludes the wrong thing. */}
                    {data.note && <p className="preview__note">{data.note}</p>}

                    {!!data.reach?.tasks?.length && (
                        <ul className="preview__tasks">
                            {data.reach.tasks.map(task => (
                                <li key={task.id}>{task.title}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ProjectAssignments({ projectId }) {
    const { data, isLoading, error } = useProjectAssignments(projectId);
    const { data: repoPage } = useRepositoriesList({ limit: PICKER_LIMIT, offset: 0, projectId });
    const { data: tierPage } = useTiersList({ limit: PICKER_LIMIT, offset: 0, projectId });

    const assign = useAssignTeam(projectId);
    const unassign = useUnassignTeam(projectId);
    const axis = useScopeAxis(projectId);

    const [teamId, setTeamId] = useState('');

    const assignments = data?.items ?? [];
    const repositories = repoPage?.items ?? [];
    const tiers = tierPage?.items ?? [];

    // Already-assigned teams are filtered out of the search itself, so the list
    // never offers one the backend would refuse.
    const assignedIds = new Set(assignments.map(a => a.team_id));
    const findTeams = (q, signal) => searchTeams(q, signal)
        .then(items => items.filter(team => !assignedIds.has(team.id)));

    const busy = assign.isPending || unassign.isPending
        || axis.addRepository.isPending || axis.removeRepository.isPending
        || axis.addTier.isPending || axis.removeTier.isPending;

    const run = async (promise, ok) => {
        try {
            await promise;
            toast.success(ok);
        } catch (err) {
            toast.error(err.message || 'No se pudo completar');
        }
    };

    if (isLoading) return <p className="assignments__loading">Cargando asignaciones...</p>;

    if (error) {
        return (
            <p className="assignments__error">
                {error.message || 'No se pudieron cargar las asignaciones'}
            </p>
        );
    }

    const nameOf = (list, id, fallback) =>
        list.find(item => item.id === id)?.name ?? `${fallback} #${id}`;

    return (
        <section className="assignments">
            <header className="assignments__header">
                <h3 className="assignments__title">Equipos asignados</h3>
                <span className="assignments__count">{assignments.length}</span>
            </header>

            {!assignments.length && (
                <p className="assignments__empty">
                    Ningún equipo asignado. Sin asignación, nadie alcanza este proyecto.
                </p>
            )}

            {assignments.map(assignment => (
                <article className="assignment" key={assignment.id}>
                    <div className="assignment__head">
                        <span className="assignment__team">
                            {assignment.team?.name ?? `Equipo #${assignment.team_id}`}
                        </span>

                        <Gate domain="projects:write">
                            <button
                                type="button"
                                className="assignment__remove"
                                onClick={() => run(
                                    unassign.mutateAsync(assignment.id),
                                    'Equipo desasignado',
                                )}
                                disabled={busy}
                            >
                                Desasignar
                            </button>
                        </Gate>
                    </div>

                    {/* The backend's sentence, verbatim. */}
                    <p className={`assignment__scope ${assignment.scope?.unrestricted ? 'assignment__scope--wide' : ''}`}>
                        {assignment.scope?.explanation}
                    </p>

                    <div className="assignment__axes">
                        <ScopeAxis
                            title="Repositorios"
                            chips={(assignment.scope?.repository_ids ?? []).map(id => ({
                                id, label: nameOf(repositories, id, 'Repo'),
                            }))}
                            options={repositories.filter(
                                repo => !(assignment.scope?.repository_ids ?? []).includes(repo.id),
                            )}
                            optionLabel={repo => repo.name || repo.slug || `#${repo.id}`}
                            onAdd={(repositoryId) => run(
                                axis.addRepository.mutateAsync({ id: assignment.id, repositoryId }),
                                'Repositorio agregado al eje',
                            )}
                            onRemove={(repositoryId) => run(
                                axis.removeRepository.mutateAsync({ id: assignment.id, repositoryId }),
                                'Repositorio sacado del eje',
                            )}
                            busy={busy}
                            emptyMeaning="sin restricción: todos los del proyecto"
                        />

                        <ScopeAxis
                            title="Tiers"
                            chips={(assignment.scope?.tier_ids ?? []).map(id => ({
                                id, label: nameOf(tiers, id, 'Tier'),
                            }))}
                            options={tiers.filter(
                                tier => !(assignment.scope?.tier_ids ?? []).includes(tier.id),
                            )}
                            optionLabel={tier => tier.name}
                            onAdd={(tierId) => run(
                                axis.addTier.mutateAsync({ id: assignment.id, tierId }),
                                'Tier agregado al eje',
                            )}
                            onRemove={(tierId) => run(
                                axis.removeTier.mutateAsync({ id: assignment.id, tierId }),
                                'Tier sacado del eje',
                            )}
                            busy={busy}
                            emptyMeaning="sin restricción: todos los del proyecto"
                        />
                    </div>

                    <Preview assignmentId={assignment.id} />
                </article>
            ))}

            <Gate domain="projects:write">
                <div className="assignments__add">
                    <span className="assignments__picker">
                        <Picker
                            value={teamId}
                            onChange={setTeamId}
                            fetchOptions={findTeams}
                            cacheKey={`project-${projectId}-teams`}
                            labelOf={team => team.name}
                            placeholder="Asignar un equipo..."
                            disabled={busy}
                        />
                    </span>
                    <button
                        type="button"
                        className="axis__add-button"
                        onClick={() => {
                            run(assign.mutateAsync(teamId), 'Equipo asignado al proyecto');
                            setTeamId('');
                        }}
                        disabled={!teamId || busy}
                    >
                        Asignar
                    </button>
                </div>
            </Gate>
        </section>
    );
}
